import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputLabel,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputAdornment,
  IconButton,
  TablePagination,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../components/layout/Layout";
import "./user.css";
import {
  deleteOrganization,
  deleteUser,
  getUsers,
  userFlagOff,
  userRegister,
  updateUser,
  updateUserAssignedScreens,
} from "../../Redux/Actions/userAction";
import { api } from "../../api/api";
import { Box } from "@mui/system";
import { styled } from "@mui/material/styles";
import {
  ChevronRight,
  DeleteOutlineOutlined,
  EditOutlined,
  ExpandMore,
  Group,
  DesktopWindowsOutlined,
  SettingsOutlined,
  Visibility,
  VisibilityOff,
  LoginOutlined,
} from "@mui/icons-material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PrimarySearchBar from "../../components/searchBar/searchbar";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import DeleteModel from "../../components/deletemodel/DeleteModel";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/loader/Loader";
import {
  ROLE,
  canManageUsers,
  isOrgAdmin,
  isOrgUser,
  isOrganization,
  isSuperAdmin,
  isSupport,
  normalizeRole,
  isViewer,
  roleDisplayLabel,
} from "../../utils/accessControl";
import { useContextUserId, useSupportContext } from "../../hooks/useSupportContext";
import { tr } from "../../i18n";

const TEAL = "#008080";

const CustomTableCell = styled(TableCell)(() => ({
  color: "#6B7280",
  fontSize: "12px",
  fontWeight: 600,
  fontFamily: "'Electrolize', sans-serif",
  letterSpacing: "0.02em",
  padding: "14px 16px",
  borderBottom: `1px solid #E5E7EB`,
  backgroundColor: "#F9FAFB",
}));

const BodyCell = styled(TableCell)(() => ({
  color: "#111827",
  fontSize: "14px",
  fontWeight: 400,
  fontFamily: "'Electrolize', sans-serif",
  lineHeight: 1.5,
  padding: "14px 16px",
  borderBottom: "1px solid #F3F4F6",
  verticalAlign: "middle",
}));

/**
 * Cards/sections for the User page.
 * — Super-admin: fixed "Organizations" then "Users" (no random parent username as a section title).
 * — Org admin: one card for their managed users.
 * — End user: "My account".
 */
function buildUserGroups(usersList, currentUser, sections) {
  if (!Array.isArray(usersList) || !currentUser) return [];

  if (isSuperAdmin(currentUser) || isSupport(currentUser)) {
    const orgRows = Array.isArray(sections?.organizations?.rows)
      ? sections.organizations.rows
      : usersList.filter((r) => normalizeRole(r.role) === ROLE.ORGANIZATION);
    /** Full member list for orgs on current page (API sections.organizationMembers), not paginated global users.rows */
    const memberRows = Array.isArray(sections?.organizationMembers?.rows)
      ? sections.organizationMembers.rows
      : Array.isArray(sections?.users?.rows)
        ? sections.users.rows
        : usersList.filter((r) =>
            [ROLE.ADMIN, ROLE.USER, ROLE.VIEWER].includes(normalizeRole(r.role))
          );
    const orgGroups = orgRows.map((org) => {
      const orgId = String(org._id);
      return {
        orgRow: org,
        id: `__org_${orgId}__`,
        organizationId: orgId,
        name: org.username || "Organization",
        subtitle: org.email || "",
        rows: memberRows.filter((m) => memberBelongsToOrg(m, orgId)),
        parentForAdd: null,
        showHeaderAdd: true,
        sectionKind: "organization-members",
      };
    });
    return orgGroups;
  }

  /** Org container account (Comforta-style): manage admins/users/viewers under this tenant */
  if (isOrganization(currentUser)) {
    const orgRootId = String(currentUser._id);
    return [
      {
        id: orgRootId,
        organizationId: orgRootId,
        name: currentUser.username || "Organization",
        subtitle: currentUser.email || "",
        rows: usersList,
        parentForAdd: currentUser._id,
        showHeaderAdd: true,
        sectionKind: "organization-members",
      },
    ];
  }

  if (isOrgAdmin(currentUser)) {
    return [
      {
        id: String(currentUser._id),
        name: "Admin",
        subtitle: currentUser.username || currentUser.email || "",
        rows: usersList,
        parentForAdd: currentUser._id,
        showHeaderAdd: true,
        sectionKind: "org-admin-members",
      },
    ];
  }

  if (isOrgUser(currentUser)) {
    return [
      {
        id: String(currentUser._id),
        name: "My account",
        rows: usersList,
        parentForAdd: null,
        showHeaderAdd: false,
      },
    ];
  }

  if (!usersList.length) return [];

  return [];
}

function isOrganizationContainerRow(row) {
  const role = normalizeRole(row?.role);
  if (role === ROLE.ORGANIZATION) return true;
  return role === ROLE.ADMIN && String(row?.organizationId || "") === String(row?._id || "");
}

function sectionCountLabel(group) {
  const n = group.sectionPagination?.totalItems ?? group.rows?.length ?? 0;
  if (group.sectionKind === "support") {
    return `${n} ${n === 1 ? "account" : "accounts"}`;
  }
  if (group.sectionKind === "organization-members") {
    return `${n} ${n === 1 ? "member" : "members"}`;
  }
  if (group.sectionKind === "orgs") {
    return `${n} ${n === 1 ? "organization" : "organizations"}`;
  }
  if (group.sectionKind === "users") {
    return `${n} ${n === 1 ? "user" : "users"}`;
  }
  return `${n} ${n === 1 ? "user" : "users"}`;
}

function roleLabel(row) {
  return roleDisplayLabel(row);
}

/**
 * Row belongs to org when organizationId matches; if org id is blank, fall back to parentId === org (legacy).
 */
function memberBelongsToOrg(memberRow, organizationIdStr) {
  const orgId = String(organizationIdStr || "").trim();
  const oid = String(memberRow?.organizationId || "").trim();
  const pid = String(memberRow?.parentId || "").trim();
  if (oid && oid === orgId) return true;
  if (oid) return false;
  return pid === orgId;
}

function userOrganizationScopeId(row) {
  const organizationId = String(row?.organizationId || "").trim();
  if (organizationId) return organizationId;
  const parentId = String(row?.parentId || "").trim();
  return parentId;
}

/** Customer/org rows Notiz Support may patch (blocked for super-admin & support accounts). */
function supportMayEditOrgMemberRow(row) {
  const r = normalizeRole(row?.role);
  if (r === ROLE.SUPPORT || r === ROLE.SUPER_ADMIN) return false;
  return String(row?.organizationId || "").trim() !== "";
}

function rowShowsUserEdit(currentUser, row) {
  if (!isSupport(currentUser)) return true;
  return supportMayEditOrgMemberRow(row);
}

/** Roles super-admin may pick when editing an existing account */
function superAdminEditRoleOptions(row) {
  const r = normalizeRole(row?.role);
  if (r === ROLE.ORGANIZATION) return [{ value: ROLE.ORGANIZATION, label: tr("Organization") }];
  if (r === ROLE.SUPPORT) return [{ value: ROLE.SUPPORT, label: tr("Notiz Support") }];
  if (r === ROLE.SUPER_ADMIN) return [{ value: ROLE.SUPER_ADMIN, label: tr("Super Admin") }];
  return [
    { value: ROLE.ADMIN, label: tr("Admin") },
    { value: ROLE.USER, label: tr("User") },
    { value: ROLE.VIEWER, label: tr("Viewer") },
  ];
}

function superAdminCanChangeRole(row) {
  const r = normalizeRole(row?.role);
  return [ROLE.ADMIN, ROLE.USER, ROLE.VIEWER].includes(r);
}

function User({ user }) {
  const contextUserId = useContextUserId();
  const { activeOrg, enter } = useSupportContext();
  /**
   * In a support context this page renders exactly as the customer's org root
   * sees it. Permission checks below use viewUser for that reason; the real
   * `user` is only used where the caller's own identity matters.
   */
  const viewUser = useMemo(
    () => (activeOrg ? { ...activeOrg, role: ROLE.ORGANIZATION } : user),
    [activeOrg, user]
  );
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!canManageUsers(viewUser)) navigate("/", { replace: true });
  }, [viewUser, navigate]);

  const [showPassword, setShowPassword] = React.useState(false);
  const [showEditPassword, setShowEditPassword] = React.useState(false);
  const defaultRole = isSuperAdmin(viewUser) ? ROLE.ORGANIZATION : ROLE.USER;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: "onTouched",
  });
  const {
    register: editRegister,
    handleSubmit: editHandleSubmit,
    reset: editReset,
    watch: editWatch,
    getValues: editGetValues,
    trigger: editTrigger,
    formState: { errors: editErrors, isValid: editIsValid },
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });
  const dispatch = useDispatch();
  const {
    isUserLoading,
    isLogging,
    users,
    isUserAdded,
    isAlreadyAvailable,
    isUserDeleted,
    loginSuccess,
    failed,
    pagination,
    sections,
  } = useSelector((state) => state.User);

  const [openForm, setOpenForm] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [openDeleteModel, setOpenDeleteModel] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({
    id: "",
    type: "user",
    name: "",
  });
  const [textVal, setTextVal] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [orgPage, setOrgPage] = useState(1);
  const [orgItemsPerPage, setOrgItemsPerPage] = useState(10);
  const [usersPage, setUsersPage] = useState(1);
  const [usersItemsPerPage, setUsersItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [role, setRole] = useState(defaultRole);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  /** Super-admin toolbar: `organization` | `member` | `notiz-support` while add dialog open; org admins leave null. */
  const [superAdminAddKind, setSuperAdminAddKind] = useState(null);
  /** Alias for super-admin “add organization” mode (keeps older references and HMR bundles valid). */
  const addingOrganization = superAdminAddKind === "organization";
  const addingNotizSupport = superAdminAddKind === "notiz-support";
  const [expanded, setExpanded] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [editTargetId, setEditTargetId] = useState(null);
  const [editTargetRow, setEditTargetRow] = useState(null);
  const [editRole, setEditRole] = useState(ROLE.USER);
  const [editOriginalEmail, setEditOriginalEmail] = useState("");
  const [screensCatalog, setScreensCatalog] = useState([]);
  const [screensLoading, setScreensLoading] = useState(false);
  const [screenAccessOpen, setScreenAccessOpen] = useState(false);
  const [screenAccessTarget, setScreenAccessTarget] = useState(null);
  const [selectedScreenIds, setSelectedScreenIds] = useState([]);
  const [screenAccessSearch, setScreenAccessSearch] = useState("");
  const [screenAccessSaving, setScreenAccessSaving] = useState(false);

  const groups = useMemo(() => buildUserGroups(users, viewUser, sections), [users, viewUser, sections]);

  useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      groups.forEach((g) => {
        if (next[g.id] === undefined) next[g.id] = true;
      });
      Object.keys(next).forEach((k) => {
        if (!groups.some((g) => String(g.id) === k)) delete next[k];
      });
      return next;
    });
  }, [groups]);

  useEffect(() => {
    if (!editOpen) return undefined;
    const sub = editWatch((_value, { name }) => {
      if (name === "password") editTrigger("confirmPassword");
    });
    return () => sub.unsubscribe();
  }, [editOpen, editWatch, editTrigger]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowEditPassword = () => setShowEditPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (loginSuccess) {
      toast.success(tr("login success!"));
      dispatch(userFlagOff());
      redirectToDashboard();
    }
    if (failed) {
      toast.error(tr("login failed!"));
      dispatch(userFlagOff());
    }
  }, [loginSuccess, failed]);

  const redirectToDashboard = () => navigate("/");

  const submitUser = async (data) => {
    if (isRegistering) return;

    const roleToSubmit = role;
    if (!roleToSubmit) {
      toast.error(tr("Role is required"));
      return;
    }
    const { email, username, password } = data;

    let payload = null;
    if (isSuperAdmin(viewUser)) {
      if (addingOrganization && roleToSubmit === ROLE.ORGANIZATION) {
        payload = {
          email,
          username: username.trim(),
          password,
          role: ROLE.ORGANIZATION,
          user: user._id,
        };
      } else if (
        superAdminAddKind === "member" &&
        [ROLE.ADMIN, ROLE.USER, ROLE.VIEWER].includes(roleToSubmit)
      ) {
        if (!selectedOrganizationId) {
          toast.error(tr("Please select organization"));
          return;
        }
        payload = {
          email,
          username: username.trim(),
          password,
          role: roleToSubmit,
          organizationId: selectedOrganizationId,
          user: user._id,
        };
      } else if (addingNotizSupport && roleToSubmit === ROLE.SUPPORT) {
        payload = {
          email,
          username: username.trim(),
          password,
          role: ROLE.SUPPORT,
          user: user._id,
        };
      } else {
        toast.error(tr("Use Add organization, Add member, or Add Notiz Support in the toolbar."));
        return;
      }
    } else {
      const orgScopeId = userOrganizationScopeId(viewUser) || String(viewUser._id);
      payload = {
        email,
        username: username.trim(),
        password,
        role: roleToSubmit,
        user: user._id,
        ...(isOrganization(viewUser) || isOrgAdmin(viewUser)
          ? { organizationId: orgScopeId }
          : {}),
      };
    }

    setIsRegistering(true);
    dispatch(userFlagOff());
    try {
      const result = await dispatch(userRegister(payload));
      if (result?.ok) {
        handleCloseForm();
        loadUsers(currentPage, itemsPerPage, searchTerm);
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const loadUsers = useCallback(
    (page = currentPage, limit = itemsPerPage, search = searchTerm) => {
      if (!user?._id) return;
      const isSuperAdminAccount = isSuperAdmin(viewUser);
      if (isSuperAdminAccount || isSupport(viewUser)) {
        dispatch(
          getUsers(contextUserId, {
            page: 1,
            limit: 1,
            search,
            orgPage,
            orgLimit: orgItemsPerPage,
            usersPage,
            usersLimit: usersItemsPerPage,
          })
        );
        return;
      }
      dispatch(getUsers(contextUserId, { page, limit, search }));
    },
    [
      user?._id,
      viewUser,
      contextUserId,
      currentPage,
      itemsPerPage,
      searchTerm,
      orgPage,
      orgItemsPerPage,
      usersPage,
      usersItemsPerPage,
      dispatch,
    ]
  );

  useEffect(() => {
    if (!user?._id || isOrgUser(user)) return;
    loadUsers(1, itemsPerPage, searchTerm);
    // Search and pagination call `loadUsers` themselves; only reload when the account is ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, user?.role]);

  useEffect(() => {
    const loadScreens = async () => {
      const canAssignScreens =
        isSuperAdmin(viewUser) || isOrgAdmin(viewUser) || isOrganization(viewUser);
      if (!canAssignScreens || !contextUserId) return;
      setScreensLoading(true);
      try {
        const response = await api.get(`/screen/getscreens/${contextUserId}?page=1&limit=500`);
        setScreensCatalog(Array.isArray(response?.data?.screens) ? response.data.screens : []);
      } catch (err) {
        const message = err.response?.data?.message || err.message;
        toast.error(message || "Could not load available screens");
      } finally {
        setScreensLoading(false);
      }
    };
    loadScreens();
  }, [contextUserId, viewUser]);

  useEffect(() => {
    if (isUserAdded) {
      dispatch(userFlagOff());
    }
    if (isAlreadyAvailable) {
      dispatch(userFlagOff());
    }
    if (isUserDeleted) {
      toast.success(tr("user deleted"));
      dispatch(userFlagOff());
      loadUsers(currentPage, itemsPerPage, searchTerm);
    }
  }, [isUserAdded, isAlreadyAvailable, isUserDeleted]);

  const askingForDelete = (id, type = "user", name = "") => {
    setDeleteTarget({ id: String(id || ""), type, name });
    setOpenDeleteModel(true);
  };
  const confirmDelete = () => {
    if (!deleteTarget.id) return;
    if (deleteTarget.type === "organization") {
      dispatch(deleteOrganization(deleteTarget.id));
    } else {
      dispatch(deleteUser(deleteTarget.id));
    }
    setOpenDeleteModel(false);
    setDeleteTarget({ id: "", type: "user", name: "" });
  };

  const searchHandler = (e) => {
    setTextVal(e);
    setSearchTerm(e);
    setCurrentPage(1);
    setOrgPage(1);
    setUsersPage(1);
    loadUsers(1, itemsPerPage, e);
  };

  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    setCurrentPage(page);
    loadUsers(page, itemsPerPage, searchTerm);
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    loadUsers(1, newItemsPerPage, searchTerm);
  };

  const handleOrgPageChange = (event, newPage) => {
    const page = newPage + 1;
    setOrgPage(page);
    dispatch(
      getUsers(contextUserId, {
        page: 1,
        limit: 1,
        search: searchTerm,
        orgPage: page,
        orgLimit: orgItemsPerPage,
        usersPage,
        usersLimit: usersItemsPerPage,
      })
    );
  };

  const handleOrgItemsPerPageChange = (event) => {
    const next = parseInt(event.target.value, 10);
    setOrgItemsPerPage(next);
    setOrgPage(1);
    dispatch(
      getUsers(contextUserId, {
        page: 1,
        limit: 1,
        search: searchTerm,
        orgPage: 1,
        orgLimit: next,
        usersPage,
        usersLimit: usersItemsPerPage,
      })
    );
  };

  const handleUsersPageChange = (event, newPage) => {
    const page = newPage + 1;
    setUsersPage(page);
    dispatch(
      getUsers(contextUserId, {
        page: 1,
        limit: 1,
        search: searchTerm,
        orgPage,
        orgLimit: orgItemsPerPage,
        usersPage: page,
        usersLimit: usersItemsPerPage,
      })
    );
  };

  const handleUsersItemsPerPageChange = (event) => {
    const next = parseInt(event.target.value, 10);
    setUsersItemsPerPage(next);
    setUsersPage(1);
    dispatch(
      getUsers(contextUserId, {
        page: 1,
        limit: 1,
        search: searchTerm,
        orgPage,
        orgLimit: orgItemsPerPage,
        usersPage: 1,
        usersLimit: next,
      })
    );
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSuperAdminAddKind(null);
    setRole(defaultRole);
    setShowPassword(false);
    reset({
      email: "",
      username: "",
      password: "",
    });
  };

  const openAddOrganizationDialog = () => {
    setShowPassword(false);
    reset({ email: "", username: "", password: "" });
    setSuperAdminAddKind("organization");
    setRole(ROLE.ORGANIZATION);
    setSelectedOrganizationId("");
    setOpenForm(true);
  };

  const openAddMemberDialog = () => {
    setShowPassword(false);
    reset({ email: "", username: "", password: "" });
    setSuperAdminAddKind("member");
    setRole(ROLE.USER);
    setSelectedOrganizationId("");
    setOpenForm(true);
  };

  const openAddNotizSupportDialog = () => {
    setShowPassword(false);
    reset({ email: "", username: "", password: "" });
    setSuperAdminAddKind("notiz-support");
    setRole(ROLE.SUPPORT);
    setSelectedOrganizationId("");
    setOpenForm(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditTargetId(null);
    setEditTargetRow(null);
    setEditRole(ROLE.USER);
    setEditOriginalEmail("");
    setShowEditPassword(false);
    editReset({ username: "", email: "", password: "", confirmPassword: "" });
  };

  const openEditUser = (row) => {
    setEditTargetId(row._id);
    setEditTargetRow(row);
    setEditRole(normalizeRole(row.role) || ROLE.USER);
    setEditOriginalEmail(row.email || "");
    setShowEditPassword(false);
    editReset({
      username: row.username || "",
      email: row.email || "",
      password: "",
      confirmPassword: "",
    });
    setEditOpen(true);
  };

  const submitEditUser = async (data) => {
    if (!editTargetId) return;
    const formData = new FormData();
    formData.append("username", data.username.trim());
    formData.append("email", data.email.trim());
    const newPassword = (data.password || "").trim();
    if (newPassword) {
      formData.append("password", newPassword);
    }
    if (isSuperAdmin(viewUser) && editTargetRow && superAdminCanChangeRole(editTargetRow)) {
      formData.append("role", editRole);
    }
    const updated = await dispatch(updateUser(editTargetId, formData));
    if (updated) {
      const roleChanged =
        isSuperAdmin(viewUser) &&
        editTargetRow &&
        superAdminCanChangeRole(editTargetRow) &&
        normalizeRole(updated.role || editRole) !== normalizeRole(editTargetRow.role);
      const emailChanged =
        data.email.trim().toLowerCase() !== (editOriginalEmail || "").trim().toLowerCase();
      const passwordChanged = Boolean(newPassword);
      if (roleChanged) {
        toast.success(`Role updated to ${roleLabel({ role: updated.role || editRole })}`);
      } else if (emailChanged && passwordChanged) {
        toast.success(
          tr("User updated. They should sign in with the new email and password next time.")
        );
      } else if (passwordChanged) {
        toast.success(tr("User updated. They should use the new password next time they sign in."));
      } else if (emailChanged) {
        toast.success(tr("User updated. Sign in with your new email next time you log in."));
      } else {
        toast.success(tr("User updated"));
      }
      handleCloseEdit();
      loadUsers(currentPage, itemsPerPage, searchTerm);
    }
  };

  const openAddUserForGroup = () => {
    setShowPassword(false);
    reset({ email: "", username: "", password: "" });
    setSuperAdminAddKind(null);
    setRole(ROLE.USER);
    setOpenForm(true);
  };

  const openAddMemberForOrganization = (organizationId) => {
    setShowPassword(false);
    reset({ email: "", username: "", password: "" });
    setSuperAdminAddKind("member");
    setRole(ROLE.USER);
    setSelectedOrganizationId(String(organizationId || ""));
    setOpenForm(true);
  };

  const canAssignScreensToMember = useCallback(
    (row) => {
      if (!row?._id) return false;
      if (isSuperAdmin(viewUser)) {
        const rowRole = normalizeRole(row?.role);
        return [ROLE.ADMIN, ROLE.USER, ROLE.VIEWER].includes(rowRole);
      }
      if (isOrgAdmin(viewUser) || isOrganization(viewUser)) {
        const rowRole = normalizeRole(row?.role);
        return rowRole === ROLE.USER || rowRole === ROLE.VIEWER;
      }
      return false;
    },
    [viewUser]
  );

  const openScreenAccessDialog = (row) => {
    if (!canAssignScreensToMember(row)) return;
    setScreenAccessTarget(row);
    setSelectedScreenIds((row?.assignedScreens || []).map((id) => String(id)));
    setScreenAccessSearch("");
    setScreenAccessOpen(true);
  };

  const closeScreenAccessDialog = () => {
    setScreenAccessOpen(false);
    setScreenAccessTarget(null);
    setSelectedScreenIds([]);
    setScreenAccessSearch("");
    setScreenAccessSaving(false);
  };

  const toggleScreenSelection = (screenId) => {
    const id = String(screenId);
    setSelectedScreenIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const scopedScreensCatalog = useMemo(() => {
    const callerOrgScopeId = userOrganizationScopeId(viewUser);
    const targetOrgScopeId = userOrganizationScopeId(screenAccessTarget);
    const orgScopeId = targetOrgScopeId || callerOrgScopeId;
    if (!orgScopeId) return [];
    return screensCatalog.filter((screen) => {
      const owner = screen?.user;
      const ownerOrgScopeId = userOrganizationScopeId(owner);
      return ownerOrgScopeId === orgScopeId;
    });
  }, [screensCatalog, screenAccessTarget, viewUser]);

  const allowedScreenIds = useMemo(
    () => new Set(scopedScreensCatalog.map((screen) => String(screen._id))),
    [scopedScreensCatalog]
  );

  const visibleScreens = useMemo(() => {
    const q = (screenAccessSearch || "").trim().toLowerCase();
    if (!q) return scopedScreensCatalog;
    return scopedScreensCatalog.filter((screen) => {
      const name = String(screen?.name || "").toLowerCase();
      const mac = String(screen?.mac || "").toLowerCase();
      const code = String(screen?.code || "").toLowerCase();
      return name.includes(q) || mac.includes(q) || code.includes(q);
    });
  }, [scopedScreensCatalog, screenAccessSearch]);

  const visibleScreenIds = useMemo(
    () => visibleScreens.map((s) => String(s._id)),
    [visibleScreens]
  );
  const allVisibleScreensSelected =
    visibleScreenIds.length > 0 &&
    visibleScreenIds.every((id) => selectedScreenIds.includes(id));
  const someVisibleScreensSelected = visibleScreenIds.some((id) =>
    selectedScreenIds.includes(id)
  );

  const handleSelectAllVisibleChange = (event) => {
    const checked = event.target.checked;
    if (checked) {
      setSelectedScreenIds((prev) => [...new Set([...prev, ...visibleScreenIds])]);
    } else {
      setSelectedScreenIds((prev) => prev.filter((id) => !visibleScreenIds.includes(id)));
    }
  };

  const handleClearAllScreenSelection = () => {
    setSelectedScreenIds([]);
  };

  const saveScreenAccess = async () => {
    if (!screenAccessTarget?._id) return;
    const scopedSelections = selectedScreenIds.filter((id) => allowedScreenIds.has(String(id)));
    setScreenAccessSaving(true);
    const result = await dispatch(
      updateUserAssignedScreens(screenAccessTarget._id, scopedSelections)
    );
    setScreenAccessSaving(false);
    if (!result) return;
    if (Array.isArray(result.invalidScreens) && result.invalidScreens.length > 0) {
      toast.error(tr("Some old screen selections were removed because they no longer exist."));
    } else {
      toast.success(tr("Screen access updated"));
    }
    closeScreenAccessDialog();
    loadUsers(currentPage, itemsPerPage, searchTerm);
  };

  const toggleGroup = (id) => {
    setExpanded((p) => ({ ...p, [id]: !p[id] }));
  };

  const roleBadgeClass = (row) => {
    if (isViewer(row)) return "user-role-pill user-role-pill--viewer";
    if (normalizeRole(row.role) === ROLE.SUPPORT)
      return "user-role-pill user-role-pill--support";
    const isAdminSide =
      normalizeRole(row.role) === ROLE.ADMIN ||
      normalizeRole(row.role) === ROLE.ORGANIZATION ||
      normalizeRole(row.role) === ROLE.SUPER_ADMIN;
    return isAdminSide ? "user-role-pill user-role-pill--admin" : "user-role-pill user-role-pill--user";
  };

  return (
    <>
      <Layout title={isOrgUser(user) ? tr("My account") : tr("User")} user={user}>
        <div className="user-page-shell">
          <div className="user-search-row">
            <div className="user-search-standalone">
              <PrimarySearchBar
                value={textVal}
                onChange={searchHandler}
                placeholder={tr("Search organization or user...")}
              />
            </div>
            {isSuperAdmin(viewUser) && (
              <div className="user-superadmin-actions">
                <button
                  type="button"
                  className="user-superadmin-btn user-superadmin-btn--primary"
                  onClick={() => openAddOrganizationDialog()}
                >
                  {tr("Add organization")}
                </button>
                <button
                  type="button"
                  className="user-superadmin-btn user-superadmin-btn--secondary"
                  onClick={() => openAddMemberDialog()}
                >
                  {tr("Add member")}
                </button>
                <button
                  type="button"
                  className="user-superadmin-btn user-superadmin-btn--outline"
                  onClick={() => openAddNotizSupportDialog()}
                  title={tr("Staff account: cross-org help, no APK or account deletion")}
                >
                  {tr("Add Notiz Support")}
                </button>
              </div>
            )}
          </div>

          {isLogging && (
            <Box
              sx={{
                height: "100vh",
                width: "100vw",
                position: "fixed",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "rgba(0,0,0,0.5)",
                top: 0,
                left: 0,
                zIndex: 100000,
              }}
            >
              <Loader />
            </Box>
          )}

          {isUserLoading && !users.length ? (
            <Box className="user-page-loading">
              <Loader />
            </Box>
          ) : groups.length === 0 ? (
            <div className="user-org-card user-org-card--empty">
              <p className="user-org-empty-text">
                {isSuperAdmin(viewUser)
                  ? tr("No organizations yet, or none match your search. Use Add organization to create one.")
                  : isSupport(viewUser)
                    ? tr("No organizations match your search.")
                    : tr("No users found")}
              </p>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.id} className="user-org-card">
                <div className="user-org-card-header">
                  <button
                    type="button"
                    className="user-org-toggle"
                    onClick={() => toggleGroup(group.id)}
                    aria-expanded={!!expanded[group.id]}
                  >
                    {expanded[group.id] ? (
                      <ExpandMore sx={{ fontSize: 22, color: "#64748B" }} />
                    ) : (
                      <ChevronRight sx={{ fontSize: 22, color: "#64748B" }} />
                    )}
                  </button>
                  <div className="user-org-icon-wrap" aria-hidden>
                    <Group sx={{ fontSize: 20, color: "#fff" }} />
                  </div>
                  <div className="user-org-heading">
                    <div className="user-org-heading-text">
                      <span className="user-org-name">{group.name}</span>
                      {group.subtitle ? (
                        <Typography
                          component="span"
                          sx={{
                            display: "block",
                            fontSize: "13px",
                            fontWeight: 400,
                            color: "#64748B",
                            mt: 0.25,
                            fontFamily: "'Electrolize', sans-serif",
                          }}
                        >
                          {group.subtitle}
                        </Typography>
                      ) : null}
                    </div>
                    <span className="user-org-count-pill">{sectionCountLabel(group)}</span>
                  </div>
                  <div className="user-org-header-actions">
                    {(isOrgAdmin(viewUser) || isSuperAdmin(viewUser) || isOrganization(viewUser)) &&
                      group.showHeaderAdd !== false && (
                    <button
                      type="button"
                      className="user-org-add-btn"
                      onClick={() =>
                        isSuperAdmin(viewUser)
                          ? openAddMemberForOrganization(group.organizationId)
                          : openAddUserForGroup()
                      }
                    >
                      {tr("+ Add User")}
                    </button>
                    )}
                    {isSuperAdmin(viewUser) && group.sectionKind === "organization-members" && (
                      <button
                        type="button"
                        className="user-org-add-btn user-org-delete-btn"
                        onClick={() =>
                          askingForDelete(group.organizationId, "organization", group.name || "Organization")
                        }
                        title={tr("Delete organization")}
                      >
                        {tr("Delete organization")}
                      </button>
                    )}
                    {!activeOrg &&
                      (isSuperAdmin(user) || isSupport(user)) &&
                      group.sectionKind === "organization-members" &&
                      group.orgRow && (
                        <IconButton
                          size="small"
                          aria-label={`Arbeta i ${group.name || "organisationen"}`}
                          title={tr("Arbeta i denna organisation")}
                          onClick={() => {
                            enter(group.orgRow);
                            navigate("/");
                          }}
                          sx={{ color: TEAL }}
                        >
                          <LoginOutlined sx={{ fontSize: 22 }} />
                        </IconButton>
                      )}
                    <IconButton
                      size="small"
                      className="user-org-settings-btn"
                      disabled
                      aria-label={tr("Organization settings (coming soon)")}
                      title={tr("Coming soon")}
                    >
                      <SettingsOutlined sx={{ fontSize: 22, color: "#CBD5E1" }} />
                    </IconButton>
                  </div>
                </div>

                {expanded[group.id] && (
                  <div className="user-org-table-wrap">
                    <TableContainer sx={{ overflowX: "auto" }}>
                      <Table size="small" className="user-org-table" sx={{ minWidth: { xs: 280, md: 520 } }}>
                        <TableHead>
                          <TableRow>
                            <CustomTableCell>{tr("Name")}</CustomTableCell>
                            <CustomTableCell>{tr("Email")}</CustomTableCell>
                            <CustomTableCell>{tr("Role")}</CustomTableCell>
                            <CustomTableCell>{tr("Screens")}</CustomTableCell>
                            <CustomTableCell align="right">{tr("Action")}</CustomTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.rows.length === 0 ? (
                            <TableRow>
                              <BodyCell colSpan={5} sx={{ color: "#64748B", fontSize: "14px", py: 2.5 }}>
                                {group.sectionKind === "organization-members" && isSuperAdmin(viewUser)
                                  ? tr("No members yet. Use Add member in the toolbar to add Admin, User, or Viewer under this organization.")
                                  : group.sectionKind === "organization-members" && isSupport(viewUser)
                                    ? tr("No members listed for this organization.")
                                    : tr("No entries yet. Use + Add User above to invite someone.")}
                              </BodyCell>
                            </TableRow>
                          ) : (
                          group.rows.map((row) => (
                            <TableRow key={row._id} className="user-org-table-row">
                              <BodyCell>
                                <span className="user-cell-name">{row.username}</span>
                              </BodyCell>
                              <BodyCell>
                                <span className="user-cell-email">
                                  <MailOutlineIcon sx={{ fontSize: 18, color: "#94A3B8", mr: 0.75 }} />
                                  {row.email}
                                </span>
                              </BodyCell>
                              <BodyCell>
                                <span className={roleBadgeClass(row)}>{roleLabel(row)}</span>
                              </BodyCell>
                              <BodyCell>
                                {canAssignScreensToMember(row) ? (
                                  <button
                                    type="button"
                                    className="user-screens-access-pill user-screens-access-pill--clickable"
                                    onClick={() => openScreenAccessDialog(row)}
                                    title={tr("Assign screens")}
                                  >
                                    {Array.isArray(row.assignedScreens) && row.assignedScreens.length > 0
                                      ? `${row.assignedScreens.length} access · Assign`
                                      : tr("No access · Assign")}
                                  </button>
                                ) : (
                                  <span className="user-screens-access-pill">
                                    {Array.isArray(row.assignedScreens) && row.assignedScreens.length > 0
                                      ? `${row.assignedScreens.length} access`
                                      : "—"}
                                  </span>
                                )}
                              </BodyCell>
                              <BodyCell align="right">
                                <div className="user-org-actions">
                                  {canAssignScreensToMember(row) && (
                                    <IconButton
                                      size="small"
                                      onClick={() => openScreenAccessDialog(row)}
                                      aria-label={tr("Assign screens")}
                                      title={tr("Assign screens")}
                                      sx={{ color: TEAL }}
                                    >
                                      <DesktopWindowsOutlined sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  )}
                                  {rowShowsUserEdit(viewUser, row) && (
                                    <IconButton
                                      size="small"
                                      onClick={() => openEditUser(row)}
                                      aria-label={tr("Edit user")}
                                      sx={{ color: "#64748B" }}
                                    >
                                      <EditOutlined sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  )}
                                  {canManageUsers(viewUser) && !isSupport(viewUser) && (
                                    <IconButton
                                      size="small"
                                      onClick={() => askingForDelete(row._id)}
                                      aria-label={tr("Delete user")}
                                      sx={{ color: "#DC2626" }}
                                    >
                                      <DeleteOutlineOutlined sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  )}
                                </div>
                              </BodyCell>
                            </TableRow>
                          ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                )}
              </div>
            ))
          )}
          {(isSuperAdmin(viewUser) || isSupport(viewUser)) &&
            sections?.organizations?.pagination &&
            groups.length > 0 && (
              <TablePagination
                className="user-page-pagination"
                rowsPerPageOptions={[5, 10, 20, 50]}
                component="div"
                count={sections.organizations.pagination.totalItems || 0}
                rowsPerPage={orgItemsPerPage}
                page={Math.max(0, orgPage - 1)}
                onPageChange={handleOrgPageChange}
                onRowsPerPageChange={handleOrgItemsPerPageChange}
                labelRowsPerPage="Organizations per page"
                sx={{
                  mt: 1,
                  borderTop: "1px solid #E5E7EB",
                  fontFamily: "'Electrolize', sans-serif",
                  color: "#111827",
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select":
                    {
                      fontSize: "13px",
                      fontFamily: "'Electrolize', sans-serif",
                      color: "#111827",
                    },
                }}
              />
            )}

          {pagination && users.length > 0 && !isSuperAdmin(viewUser) && !isSupport(viewUser) && (
            <TablePagination
              className="user-page-pagination"
              rowsPerPageOptions={[5, 10, 20, 50]}
              component="div"
              count={pagination.totalItems || users.length}
              rowsPerPage={itemsPerPage}
              page={currentPage - 1}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleItemsPerPageChange}
              sx={{
                mt: 1,
                borderTop: "none",
                fontFamily: "'Electrolize', sans-serif",
                color: "#111827",
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select":
                  {
                    fontSize: "13px",
                    fontFamily: "'Electrolize', sans-serif",
                    color: "#111827",
                  },
              }}
            />
          )}
        </div>

        <DeleteModel
          open={openDeleteModel}
          onClose={(e) => {
            setOpenDeleteModel(e);
            if (!e) setDeleteTarget({ id: "", type: "user", name: "" });
          }}
          onConfirm={confirmDelete}
          title={deleteTarget.type === "organization" ? tr("Delete Organization") : tr("Delete User")}
          message={
            deleteTarget.type === "organization"
              ? `Are you sure you want to delete ${deleteTarget.name || "this organization"}? This action cannot be undone.`
              : "Are you sure you want to delete this user? This action cannot be undone."
          }
        />

        <Dialog
          maxWidth="sm"
          fullWidth
          open={screenAccessOpen}
          onClose={closeScreenAccessDialog}
          PaperProps={{
            sx: {
              borderRadius: "14px",
              border: "1px solid #e6edf6",
              boxShadow: "0 20px 40px rgba(15, 23, 42, 0.16)",
            },
          }}
        >
          <DialogTitle className="user-dialog-title">
            {tr("Screen access")}
          </DialogTitle>
          <DialogContent sx={{ pb: 2, pt: 1 }}>
            <Typography
              component="p"
              sx={{
                fontFamily: "'Electrolize', sans-serif",
                fontSize: "14px",
                color: "#64748B",
                mb: 1.5,
                lineHeight: 1.5,
              }}
            >
              {tr("Assign screens for")} <strong>{screenAccessTarget?.username || tr("user")}</strong>{tr(". Only selected screens will be accessible.")}
            </Typography>
            <TextField
              value={screenAccessSearch}
              onChange={(e) => setScreenAccessSearch(e.target.value)}
              size="small"
              fullWidth
              label={tr("Search by screen name, MAC or code")}
              sx={{ mb: 1.25 }}
            />
            <div className="user-screen-access-toolbar">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={allVisibleScreensSelected}
                    indeterminate={
                      someVisibleScreensSelected && !allVisibleScreensSelected
                    }
                    onChange={handleSelectAllVisibleChange}
                    disabled={screensLoading || visibleScreens.length === 0}
                  />
                }
                label={`Select all${visibleScreens.length ? ` (${visibleScreens.length})` : ""}`}
                sx={{
                  mr: 0,
                  flex: 1,
                  minWidth: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    fontWeight: 500,
                    fontFamily: "'Electrolize', sans-serif",
                    color: "#374151",
                  },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selectedScreenIds.length === 0}
                    disabled={screensLoading || selectedScreenIds.length === 0}
                    onChange={(e) => {
                      if (selectedScreenIds.length > 0 && e.target.checked) {
                        handleClearAllScreenSelection();
                      }
                    }}
                  />
                }
                label={tr("Clear")}
                sx={{
                  mr: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    fontWeight: 500,
                    fontFamily: "'Electrolize', sans-serif",
                    color: "#64748B",
                  },
                }}
              />
            </div>
            <div className="user-screen-access-list-wrap">
              {screensLoading ? (
                <Typography sx={{ p: 1.5, color: "#64748B", fontSize: "13px" }}>
                  {tr("Loading screens...")}
                </Typography>
              ) : visibleScreens.length === 0 ? (
                <Typography sx={{ p: 1.5, color: "#64748B", fontSize: "13px" }}>
                  {tr("No screens found.")}
                </Typography>
              ) : (
                <List dense sx={{ py: 0 }}>
                  {visibleScreens.map((screen) => {
                    const sid = String(screen._id);
                    const label = screen?.name || screen?.mac || `Code ${screen?.code || "Unknown"}`;
                    const details = [screen?.mac ? `MAC: ${screen.mac}` : "", screen?.code ? `Code: ${screen.code}` : ""]
                      .filter(Boolean)
                      .join(" | ");
                    return (
                      <ListItem
                        key={sid}
                        divider
                        disableGutters
                        onClick={() => toggleScreenSelection(sid)}
                        secondaryAction={
                          <Checkbox
                            edge="end"
                            checked={selectedScreenIds.includes(sid)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleScreenSelection(sid)}
                          />
                        }
                        sx={{ cursor: "pointer", pr: 1.5 }}
                      >
                        <ListItemText
                          primary={label}
                          secondary={details || "No extra details"}
                          primaryTypographyProps={{ fontSize: "13px", fontWeight: 600 }}
                          secondaryTypographyProps={{ fontSize: "12px" }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </div>
            <div className="user-edit-form-actions">
              <button type="button" className="user-edit-cancel-btn" onClick={closeScreenAccessDialog}>
                {tr("Cancel")}
              </button>
              <button
                type="button"
                className="user-edit-save-btn"
                onClick={saveScreenAccess}
                disabled={screenAccessSaving}
              >
                {screenAccessSaving ? tr("Saving...") : tr("Save access")}
              </button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          maxWidth="sm"
          fullWidth
          open={editOpen}
          onClose={handleCloseEdit}
          PaperProps={{
            sx: {
              borderRadius: "14px",
              border: "1px solid #e6edf6",
              boxShadow: "0 20px 40px rgba(15, 23, 42, 0.16)",
            },
          }}
        >
          <DialogTitle className="user-dialog-title">{tr("Edit user")}</DialogTitle>
          <DialogContent sx={{ pb: 2, pt: 0.5 }}>
            <Typography
              component="p"
              sx={{
                fontFamily: "'Electrolize', sans-serif",
                fontSize: "14px",
                color: "#64748B",
                mb: 2,
                mt: 0,
                lineHeight: 1.5,
              }}
            >
              {tr("Update name, sign-in email, and role (super admin). Other data stays linked to this account (same user ID). Leave new password blank to keep the current password.")}
            </Typography>
            <form onSubmit={editHandleSubmit(submitEditUser)}>
              <div className="user-add-form-container user-add-form-container--pro">
                {isSuperAdmin(viewUser) && editTargetRow && superAdminCanChangeRole(editTargetRow) && (
                  <div className="form-fields">
                    <FormControl variant="outlined" fullWidth size="small">
                      <InputLabel id="edit-user-role-select">{tr("Role *")}</InputLabel>
                      <Select
                        labelId="edit-user-role-select"
                        label={tr("Role *")}
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value)}
                      >
                        {superAdminEditRoleOptions(editTargetRow).map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                )}
                {isSuperAdmin(viewUser) && editTargetRow && !superAdminCanChangeRole(editTargetRow) && (
                  <div className="form-fields">
                    <TextField
                      label={tr("Role")}
                      value={roleLabel(editTargetRow)}
                      size="small"
                      fullWidth
                      disabled
                      helperText={tr("This account type cannot be changed here.")}
                    />
                  </div>
                )}
                <div className="form-fields">
                  <TextField
                    {...editRegister("username", {
                      required: "Name is required",
                      validate: (value) =>
                        value.trim().length >= 2 || "Name must be at least 2 characters",
                    })}
                    type="text"
                    variant="outlined"
                    size="small"
                    label={tr("Name *")}
                    fullWidth
                    error={Boolean(editErrors.username)}
                    helperText={editErrors.username?.message}
                  />
                </div>
                <div className="form-fields">
                  <TextField
                    {...editRegister("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Enter a valid email address",
                      },
                    })}
                    type="email"
                    variant="outlined"
                    size="small"
                    label={tr("Email *")}
                    fullWidth
                    error={Boolean(editErrors.email)}
                    helperText={editErrors.email?.message}
                  />
                </div>
                <div className="form-fields">
                  <TextField
                    {...editRegister("password", {
                      validate: (value) => {
                        const v = value || "";
                        if (!v.trim()) return true;
                        return v.length >= 6 || "Password must be at least 6 characters";
                      },
                    })}
                    type={showEditPassword ? "text" : "password"}
                    variant="outlined"
                    size="small"
                    label={tr("New password (optional)")}
                    fullWidth
                    autoComplete="new-password"
                    error={Boolean(editErrors.password)}
                    helperText={editErrors.password?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={tr("toggle new password visibility")}
                            onClick={handleClickShowEditPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showEditPassword ? (
                              <VisibilityOff sx={{ color: TEAL }} />
                            ) : (
                              <Visibility sx={{ color: TEAL }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <div className="form-fields">
                  <TextField
                    {...editRegister("confirmPassword", {
                      validate: (value) => {
                        const pwd = editGetValues("password") || "";
                        if (!pwd.trim()) return true;
                        return value === pwd || "Passwords do not match";
                      },
                    })}
                    type={showEditPassword ? "text" : "password"}
                    variant="outlined"
                    size="small"
                    label={tr("Confirm new password")}
                    fullWidth
                    autoComplete="new-password"
                    error={Boolean(editErrors.confirmPassword)}
                    helperText={editErrors.confirmPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={tr("toggle confirm password visibility")}
                            onClick={handleClickShowEditPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showEditPassword ? (
                              <VisibilityOff sx={{ color: TEAL }} />
                            ) : (
                              <Visibility sx={{ color: TEAL }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <div className="user-edit-form-actions">
                  <button type="button" className="user-edit-cancel-btn" onClick={handleCloseEdit}>
                    {tr("Cancel")}
                  </button>
                  <button type="submit" className="user-edit-save-btn" disabled={!editIsValid}>
                    {tr("Save changes")}
                  </button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog
          maxWidth="sm"
          fullWidth
          open={openForm}
          onClose={handleCloseForm}
          PaperProps={{
            sx: {
              borderRadius: "14px",
              border: "1px solid #e6edf6",
              boxShadow: "0 20px 40px rgba(15, 23, 42, 0.16)",
            },
          }}
        >
          <DialogTitle className="user-dialog-title">
            {isSuperAdmin(viewUser) && addingOrganization
              ? tr("Add organization")
              : isSuperAdmin(viewUser) && superAdminAddKind === "member"
                ? tr("Add member")
                : isSuperAdmin(viewUser) && addingNotizSupport
                  ? tr("Add Notiz Support")
                : tr("Add User")}
          </DialogTitle>
          <DialogContent sx={{ pb: 2 }}>
            <form onSubmit={handleSubmit(submitUser)}>
              <div className="user-add-form-container user-add-form-container--pro">
                {isSuperAdmin(viewUser) && addingOrganization && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748B", mb: 2, fontFamily: "'Electrolize', sans-serif" }}
                  >
                    {tr("Creates a new organization account. They sign in and add their own users from their User page.")}
                  </Typography>
                )}
                {isSuperAdmin(viewUser) && superAdminAddKind === "member" && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748B", mb: 2, fontFamily: "'Electrolize', sans-serif" }}
                  >
                    {tr("Creates members under a selected organization. Choose role as Admin, User or Viewer.")}
                  </Typography>
                )}
                {isSuperAdmin(viewUser) && addingNotizSupport && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748B", mb: 2, fontFamily: "'Electrolize', sans-serif" }}
                  >
                    {tr("Creates a Notiz staff support account. They can open organizations and help with content, screens, and playlists. They cannot upload APKs, delete accounts, or change system-only settings.")}
                  </Typography>
                )}
                <div className="form-fields">
                  <TextField
                    {...register("username", {
                      required: "Name is required",
                      validate: (value) =>
                        value.trim().length >= 2 || "Name must be at least 2 characters",
                    })}
                    type="text"
                    variant="outlined"
                    size="small"
                    label={tr("Name *")}
                    fullWidth
                    error={Boolean(errors.username)}
                    helperText={errors.username?.message}
                  />
                </div>
                <div className="form-fields">
                  <TextField
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Enter a valid email address",
                      },
                    })}
                    type="email"
                    variant="outlined"
                    size="small"
                    label={tr("Email *")}
                    fullWidth
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                  />
                </div>
                <div className="form-fields">
                  <TextField
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters",
                      },
                    })}
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    size="small"
                    label={tr("Password *")}
                    fullWidth
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={tr("toggle password visibility")}
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showPassword ? (
                              <VisibilityOff sx={{ color: TEAL }} />
                            ) : (
                              <Visibility sx={{ color: TEAL }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                <div className="form-fields">
                  {(isOrgAdmin(viewUser) || isOrganization(viewUser)) && (
                    <FormControl variant="outlined" fullWidth size="small">
                      <InputLabel id="org-root-role-select">{tr("Role *")}</InputLabel>
                      <Select
                        labelId="org-root-role-select"
                        value={role}
                        label={tr("Role *")}
                        onChange={(e) => setRole(e.target.value)}
                      >
                        <MenuItem value={ROLE.ADMIN}>{tr("Admin")}</MenuItem>
                        <MenuItem value={ROLE.USER}>{tr("User")}</MenuItem>
                        <MenuItem value={ROLE.VIEWER}>{tr("Viewer")}</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  {isSuperAdmin(viewUser) && addingOrganization && (
                    <FormControl variant="outlined" fullWidth size="small">
                      <TextField
                        label={tr("Role *")}
                        type="text"
                        value="Organization"
                        disabled
                        size="small"
                        fullWidth
                      />
                    </FormControl>
                  )}
                  {isSuperAdmin(viewUser) && superAdminAddKind === "member" && (
                    <FormControl variant="outlined" fullWidth size="small">
                      <InputLabel id="user-role-select">{tr("Role *")}</InputLabel>
                      <Select
                        labelId="user-role-select"
                        value={role}
                        label={tr("Role *")}
                        onChange={(e) => setRole(e.target.value)}
                      >
                        <MenuItem value={ROLE.ADMIN}>{tr("Admin")}</MenuItem>
                        <MenuItem value={ROLE.USER}>{tr("User")}</MenuItem>
                        <MenuItem value={ROLE.VIEWER}>{tr("Viewer")}</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  {isSuperAdmin(viewUser) && addingNotizSupport && (
                    <FormControl variant="outlined" fullWidth size="small">
                      <TextField
                        label={tr("Role *")}
                        type="text"
                        value="Notiz Support"
                        disabled
                        size="small"
                        fullWidth
                      />
                    </FormControl>
                  )}
                  {isSuperAdmin(viewUser) && superAdminAddKind === "member" && (
                    <FormControl variant="outlined" fullWidth size="small" sx={{ mt: 1.25 }}>
                      <InputLabel id="organization-select">{tr("Organization *")}</InputLabel>
                      <Select
                        labelId="organization-select"
                        value={selectedOrganizationId}
                        label={tr("Organization *")}
                        onChange={(e) => setSelectedOrganizationId(e.target.value)}
                      >
                        {(sections?.organizations?.rows || [])
                          .filter((o) => isOrganizationContainerRow(o))
                          .map((o) => (
                            <MenuItem key={o._id} value={o._id}>
                              {o.username}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}
                </div>

                <div className="add-user-form-submit-btn">
                  <button type="submit" disabled={!isValid || isRegistering}>
                    {isSuperAdmin(viewUser) && addingOrganization
                      ? tr("Add organization")
                      : isSuperAdmin(viewUser) && superAdminAddKind === "member"
                        ? tr("Add member")
                        : isSuperAdmin(viewUser) && addingNotizSupport
                          ? tr("Add Notiz Support")
                        : tr("Add User")}
                  </button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </Layout>
    </>
  );
}

export default User;
